import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import CloseButton from "../../common/CloseButton";

const DateFilters = {
    LAST_30DAYS: 1,
    ALL_DATES: 2,
};

const SortBy = {
    COUNT: 0,
    REASON: 1,
};

const Order = {
    ASC: "ASC",
    DESC: "DESC",
};

const OrderBy = {
    COUNT: {
        sortBy: SortBy.COUNT,
        order: Order.DESC,
    },
    REASON: {
        sortBy: SortBy.REASON,
        order: Order.ASC,
    },
};

const CaretIcon = ({ name }) => {
    const Icons = {
        down: () => (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                height="1em"
                width="1em"
                viewBox="0 0 48 48"
            >
                <path d="m24 30-10-9.95h20Z" fill="currentColor" />
            </svg>
        ),
        up: () => (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                height="1em"
                width="1em"
                viewBox="0 0 48 48"
            >
                <path d="m14 28 10-10.05L34 28Z" fill="currentColor" />
            </svg>
        ),
    };
    const SelectedIcon = Icons[name];

    return <SelectedIcon />;
};

/**
 * @typedef {Object} UnfollowReasonModal
 * @prop {boolean} show - show state of the modal
 * @prop {() => void} setShow - a setState function that hides the modal
 * @prop {import("react-bootstrap").ModalDialogProps} modalProps
 * @prop {import("react-bootstrap/esm/ModalHeader").ModalHeaderProps} modalHeaderProps
 * @prop {Modal} modalHeaderProps
 */

/**
 * @param {UnfollowReasonModal & import('react').HTMLProps} param0
 * @example
 * const [showModal, setShowModal] = useState(false);
 * ...
 * ...
 * <UnfollowReasonModal
 *    setShow={setShowModal}
 *    show={showModal}
 * />
 * @returns
 */
export default function UnfollowReasonModal({
    show,
    setShow,
    modalProps,
    modalHeaderProps,
}) {
    const [list, setList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [dateFilter, setDateFilter] = useState(DateFilters.LAST_30DAYS);
    const [orderBy, setOrderBy] = useState({ ...OrderBy.COUNT });
    const orderValues = useRef(JSON.parse(JSON.stringify(OrderBy)));

    useEffect(() => {
        setIsLoading(true);
        axios
            .get("/api/followers/unfollow-summary", {
                params: {
                    dateFilter,
                    ...orderBy,
                },
            })
            .then((res) => {
                console.log(res.data);
                setList(res.data);
                setIsLoading(false);
            })
            .catch((err) => {
                console.log(err);
            });
    }, [dateFilter, orderBy]);

    const handleHide = () => {
        typeof setShow === "function" && setShow(false);
    };

    const handleDateChange = (e) => {
        e.preventDefault();
        setDateFilter(Number(e.target.value));
    };

    const handleHeaderClick = (headerKey) => {
        orderValues.current[headerKey] = {
            ...orderValues.current[headerKey],
            ...(orderBy.sortBy === orderValues.current[headerKey].sortBy &&
                (orderValues.current[headerKey].order === Order.ASC
                    ? { order: Order.DESC }
                    : { order: Order.ASC })),
        };

        setOrderBy(orderValues.current[headerKey]);
    };

    return (
        <Modal
            show={show}
            onHide={handleHide}
            backdrop={true}
            centered
            {...modalProps}
        >
            {/* <Modal.Header {...modalHeaderProps}>
                Summary
                <CloseButton
                    onClick={handleHide}
                    style={{
                        position: "absolute",
                        top: "12px",
                        right: "12px",
                    }}
                    // disabled={isProcessing}
                />
            </Modal.Header> */}
            <Modal.Body
                className="followers-total-table-content"
                style={{ minHeight: 0 }}
            >
                <CloseButton
                    onClick={handleHide}
                    style={{
                        position: "absolute",
                        top: "12px",
                        right: "12px",
                    }}
                    // disabled={isProcessing}
                />
                <h4 className="text-center mt-2">Unfollow Reasons Summary</h4>
                <select
                    className="follower-search-filter mt-4"
                    onChange={handleDateChange}
                    value={dateFilter}
                >
                    <option value={DateFilters.LAST_30DAYS}>
                        Last 30 Days
                    </option>
                    <option value={DateFilters.ALL_DATES}>All Dates</option>
                </select>
                <table className="table table-bordered followers-tbl mt-2">
                    <thead>
                        <tr>
                            <th
                                className="th-middle"
                                onClick={() => handleHeaderClick("COUNT")}
                            >
                                <div className="th-middle-v-center">
                                    Count{" "}
                                    {orderBy.sortBy === SortBy.COUNT && (
                                        <div className="th-middle-ico-center">
                                            {orderBy.order === "ASC" ? (
                                                <CaretIcon name="up" />
                                            ) : (
                                                <CaretIcon name="down" />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </th>
                            <th
                                className="th-middle"
                                onClick={() => handleHeaderClick("REASON")}
                            >
                                <div className="th-middle-v-center">
                                    Unfollow Reason{" "}
                                    {orderBy.sortBy === SortBy.REASON && (
                                        <div className="th-middle-ico-center">
                                            {orderBy.order === "ASC" ? (
                                                <CaretIcon name="up" />
                                            ) : (
                                                <CaretIcon name="down" />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading && (
                            <tr>
                                <td colSpan={2} style={{ textAlign: "center" }}>
                                    <Spinner
                                        variant="primary"
                                        size="sm"
                                        animation="border"
                                    />
                                </td>
                            </tr>
                        )}
                        {isLoading === false &&
                            (Array.isArray(list) && list.length > 0 ? (
                                list.map((item, index) => (
                                    <tr key={index}>
                                        <td>{Number(item.count).toLocaleString()}</td>
                                        <td>
                                            {item.unfollow_reason ||
                                                "No reason provided"}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan={2}
                                        style={{ textAlign: "center" }}
                                    >
                                        No Record Found
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </Modal.Body>
        </Modal>
    );
}
